import { Navigate, Outlet } from 'react-router-dom';
import { Layout, Loader } from '@/components';
import { PUBLIC_PATH } from '@/constants';
import { homePathFor } from '@/constants/paths';
import type { ProtectedRouteProps } from './ProtectedRoute.types';
import { useAppSelector } from '@/hooks/useAppSelector';
import { selectAuthState } from '@/selectors/auth';

/**
 * Shared authenticated shell. Mounted once for all private routes so navigating
 * between them does not tear down / rebuild the Layout (which caused a white flash).
 * Per-route role checks are done by <RoleGuard>.
 */
export const AuthenticatedShell = (): JSX.Element => {
  const { user, isLoading } = useAppSelector(selectAuthState);

  if (isLoading) {
    return <Loader />;
  }

  if (!user) {
    return <Navigate to={{ pathname: PUBLIC_PATH.LOGIN }} />;
  }

  return (
    <Layout user={user}>
      <Outlet />
    </Layout>
  );
};

/** Per-route role check. Renders <Outlet /> when allowed; otherwise a signed-in
 *  user goes to their own home page and anyone else to login. */
const ProtectedRoute = ({ allowedRoles }: ProtectedRouteProps): JSX.Element => {
  const { user } = useAppSelector(selectAuthState);

  if (user && allowedRoles.includes(user.role)) {
    return <Outlet />;
  }

  return <Navigate to={{ pathname: user ? homePathFor(user.role) : PUBLIC_PATH.LOGIN }} replace />;
};

export default ProtectedRoute;
